import React, { useState } from 'react'
import { WrenchIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { useHistory } from '../../contexts/HistoryContext'
import { handlePostRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { useAuth } from '../../contexts/AuthContext'
import DisplayForUserType from '../../components/auth/DisplayForUserType'
import USER_TYPE from '../../constants/userType'
import { useNavigate } from 'react-router-dom'

const ServerErrorPage = () => {
  const { currentUser } = useAuth()
  const { getPreviousPage } = useHistory()
  const [supportTicketSent, setSupportTicketSent] = useState(false)
  const navigate = useNavigate()

  const contactSupportMutation = useMutation({
    mutationFn: () => {
      const previousPage = getPreviousPage()
      return handlePostRequest(`/${currentUser?.userType}s/support/tickets`, {
        subject: 'Server Error',
        description: `A server error occurred on the page: ${previousPage}. The user was redirected to the error page.`,
      })
    },
    onSuccess: () => {
      setSupportTicketSent(true)
      showToaster('success', 'Support ticket sent successfully!')
    },
    onError: () => {
      showToaster('error', 'Failed to send support ticket. Please try again.')
    },
  })

  const handleContactSupport = async () => {
    await contactSupportMutation.mutateAsync()
  }

  return (
    <div className="min-h-screen bg-neutral-200-50 flex items-center justify-center px-4">
      <div className="bg-background p-16 rounded-lg shadow-lg max-w-md w-full text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-red-600 rounded-full p-5 inline-flex items-center justify-center">
            <WrenchIcon className="h-12 w-12 text-white" />
          </div>
        </div>
        <h1 className="text-xl font-bold text-orange-600 mb-2">Oops! Something's not right</h1>
        <p className="text-gray-600 text-sm mb-6">
          We've hit a snag and notified our engineers. We will investigate and fix this soon. In the
          meantime, try refreshing as that fixes it sometimes or wait a few minutes and try again.
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => navigate('/')}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded transition"
          >
            Go Home
          </button>
          <DisplayForUserType
            userTypes={[USER_TYPE.customer, USER_TYPE.retailer, USER_TYPE.wholeseller]}
          >
            {!supportTicketSent ? (
              <button
                onClick={handleContactSupport}
                disabled={contactSupportMutation.isPending}
                className={`border border-orange-500 text-orange-500 hover:bg-orange-50 font-semibold py-2 px-4 rounded transition ${
                  contactSupportMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {contactSupportMutation.isPending ? 'Sending...' : 'Contact Support'}
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 py-2 px-4 text-green-600 bg-green-50 border border-green-200 rounded">
                <CheckCircleIcon className="w-5 h-5" />
                <span className="font-semibold">Support message sent!</span>
              </div>
            )}
          </DisplayForUserType>
        </div>
      </div>
    </div>
  )
}

export default ServerErrorPage
